import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getKnowledgeBase,
  getKnowledgeBaseFiles,
  triggerKnowledgeSync,
  updateDocumentUsageType,
} from '../../api/knowledgeBases';
import EmptyState from '../../components/ui/EmptyState';
import { EMPTY_STATE_ICONS } from '../../support/iconMappings';
import { getApiErrorMessage } from '../../utils/errors';
import ImportFileStatusBadge from './components/ImportFileStatusBadge';
import KnowledgeStatusBadge from './components/KnowledgeStatusBadge';
import SyncButton from './components/SyncButton';
import {
  KB_USAGE_TYPE_OPTIONS,
  SOURCE_TYPE_LABELS,
} from './constants';
import {
  buildSourceReference,
  fileInSource,
  formatDate,
  formatSyncSummary,
  isProcessingStatus,
  isSourceCheckStale,
  sortFilesBySourcePresence,
} from './utils';

function OverviewField({ label, children }) {
  return (
    <div>
      <dt className="text-xs font-medium text-content-muted">{label}</dt>
      <dd className="text-sm text-content-primary mt-1 break-all">{children}</dd>
    </div>
  );
}

function SourcePresenceBadge({ inSource }) {
  if (inSource) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">
        Na fonte
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border bg-amber-50 text-amber-800 border-amber-200">
      Removido da fonte
    </span>
  );
}

function canEditFileUsageType(file) {
  return file.status === 'completed' && Boolean(file.document_id) && fileInSource(file);
}

function FileUsageSelect({ file, saving, onChange }) {
  if (!canEditFileUsageType(file)) {
    const label =
      KB_USAGE_TYPE_OPTIONS.find((o) => o.value === (file.usage_type || 'context'))?.label
      || 'Contexto';
    return <span className="text-content-muted">{label}</span>;
  }

  return (
    <select
      value={file.usage_type || 'context'}
      onChange={(event) => onChange(file, event.target.value)}
      disabled={saving}
      className="block w-full max-w-[9rem] rounded-lg border border-surface-border bg-white px-2 py-1.5 text-sm text-content-primary focus:border-forge-500 focus:ring-forge-500 disabled:opacity-50"
      aria-label={`Uso do arquivo ${file.file_name}`}
    >
      {KB_USAGE_TYPE_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function FilesTable({ files, savingDocumentId, onUsageTypeChange }) {
  const sortedFiles = sortFilesBySourcePresence(files);

  if (sortedFiles.length === 0) {
    return (
      <EmptyState
        icon={EMPTY_STATE_ICONS.files}
        title="Nenhum arquivo encontrado"
        description="Os arquivos importados aparecerão aqui após a sincronização."
      />
    );
  }

  return (
    <>
      <div className="hidden md:block surface-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-surface-border bg-slate-50 text-left">
              <th className="px-4 py-3 font-medium text-content-muted">Nome</th>
              <th className="px-4 py-3 font-medium text-content-muted">Formato</th>
              <th className="px-4 py-3 font-medium text-content-muted">Uso</th>
              <th className="px-4 py-3 font-medium text-content-muted">Status</th>
              <th className="px-4 py-3 font-medium text-content-muted">Na fonte</th>
              <th className="px-4 py-3 font-medium text-content-muted">Atualizado</th>
              <th className="px-4 py-3 font-medium text-content-muted">Erro</th>
            </tr>
          </thead>
          <tbody>
            {sortedFiles.map((file) => {
              const inSource = fileInSource(file);
              return (
              <tr
                key={file.id}
                className={`border-b border-surface-border last:border-b-0 hover:bg-slate-50/80 transition-colors ${
                  inSource ? '' : 'opacity-70'
                }`}
              >
                <td className={`px-4 py-3 font-medium ${inSource ? 'text-content-primary' : 'text-content-muted'}`}>
                  {file.file_name}
                </td>
                <td className="px-4 py-3 text-content-muted">{file.file_type || '—'}</td>
                <td className="px-4 py-3">
                  <FileUsageSelect
                    file={file}
                    saving={savingDocumentId === file.document_id}
                    onChange={onUsageTypeChange}
                  />
                </td>
                <td className="px-4 py-3">
                  <ImportFileStatusBadge status={file.status} />
                </td>
                <td className="px-4 py-3">
                  <SourcePresenceBadge inSource={inSource} />
                </td>
                <td className="px-4 py-3 text-content-muted whitespace-nowrap">
                  {formatDate(file.updated_at)}
                </td>
                <td className="px-4 py-3 text-content-muted text-xs max-w-xs truncate">
                  {file.error_message || '—'}
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3">
        {sortedFiles.map((file) => {
          const inSource = fileInSource(file);
          return (
          <div
            key={file.id}
            className={`surface-card p-4 space-y-2 ${inSource ? '' : 'opacity-70'}`}
          >
            <div className="flex items-start justify-between gap-3">
              <p className={`font-medium break-all ${inSource ? 'text-content-primary' : 'text-content-muted'}`}>
                {file.file_name}
              </p>
              <ImportFileStatusBadge status={file.status} />
            </div>
            <p className="text-xs text-content-muted">{file.file_type || 'Formato desconhecido'}</p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-content-muted">Uso:</span>
              <FileUsageSelect
                file={file}
                saving={savingDocumentId === file.document_id}
                onChange={onUsageTypeChange}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <SourcePresenceBadge inSource={inSource} />
              <span className="text-xs text-content-subtle">{formatDate(file.updated_at)}</span>
            </div>
            {file.error_message && (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {file.error_message}
              </p>
            )}
          </div>
        );
        })}
      </div>
    </>
  );
}

function KnowledgeBaseShow() {
  const { id } = useParams();
  const [kb, setKb] = useState(null);
  const [filesData, setFilesData] = useState({ files: [], import_job_status: null });
  const [loading, setLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(true);
  const [error, setError] = useState('');
  const [filesError, setFilesError] = useState('');
  const [syncMessage, setSyncMessage] = useState('');
  const [syncError, setSyncError] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [savingDocumentId, setSavingDocumentId] = useState(null);
  const [usageTypeError, setUsageTypeError] = useState('');
  const refreshTimeoutsRef = useRef([]);
  const pollingRef = useRef(null);

  const clearRefreshTimeouts = useCallback(() => {
    refreshTimeoutsRef.current.forEach(clearTimeout);
    refreshTimeoutsRef.current = [];
  }, []);

  const loadKb = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }

    try {
      const data = await getKnowledgeBase(id);
      setKb(data);
      return data;
    } catch (err) {
      if (!silent) {
        setError(getApiErrorMessage(err, 'Não foi possível carregar a base.'));
        setKb(null);
      }
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id]);

  const loadFiles = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setFilesLoading(true);
      setFilesError('');
    }

    try {
      const data = await getKnowledgeBaseFiles(id);
      setFilesData(data);
    } catch (err) {
      if (!silent) {
        setFilesError(getApiErrorMessage(err, 'Não foi possível carregar os arquivos.'));
        setFilesData({ files: [], import_job_status: null });
      }
    } finally {
      if (!silent) {
        setFilesLoading(false);
      }
    }
  }, [id]);

  const loadAll = useCallback(
    async ({ silent = false } = {}) => {
      await Promise.all([loadKb({ silent }), loadFiles({ silent })]);
    },
    [loadKb, loadFiles]
  );

  const scheduleStatusRefresh = useCallback(() => {
    clearRefreshTimeouts();
    [3000, 8000].forEach((delay) => {
      const timeoutId = setTimeout(() => {
        loadAll({ silent: true });
      }, delay);
      refreshTimeoutsRef.current.push(timeoutId);
    });
  }, [clearRefreshTimeouts, loadAll]);

  useEffect(() => {
    loadAll();
    return () => {
      clearRefreshTimeouts();
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [loadAll, clearRefreshTimeouts]);

  useEffect(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    if (!kb || !isProcessingStatus(kb.status)) {
      return undefined;
    }

    pollingRef.current = setInterval(() => {
      loadAll({ silent: true });
    }, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [kb?.status, loadAll]);

  const handleFileUsageTypeChange = async (file, nextUsageType) => {
    if (!kb || !file.document_id || nextUsageType === (file.usage_type || 'context')) {
      return;
    }

    setUsageTypeError('');
    setSavingDocumentId(file.document_id);

    try {
      await updateDocumentUsageType(kb.id, file.document_id, nextUsageType);
      setFilesData((current) => ({
        ...current,
        files: (current.files || []).map((item) =>
          item.document_id === file.document_id
            ? { ...item, usage_type: nextUsageType }
            : item
        ),
      }));
      setSyncMessage(
        'Uso do arquivo atualizado. Recomendamos sincronizar a base para atualizar o índice de busca.'
      );
    } catch (err) {
      setUsageTypeError(
        getApiErrorMessage(err, 'Não foi possível atualizar o uso do arquivo.')
      );
    } finally {
      setSavingDocumentId(null);
    }
  };

  const handleSync = async (knowledgeBase) => {
    const previousStatus = knowledgeBase.status;
    const checksSource =
      knowledgeBase.source_type === 's3' || knowledgeBase.source_type === 'google_drive';
    setSyncMessage('');
    setSyncError('');
    setIsSyncing(true);
    setKb((current) => (current ? { ...current, status: 'syncing' } : current));

    try {
      await triggerKnowledgeSync(knowledgeBase.id);
      setSyncMessage(
        checksSource
          ? `Verificando atualizações na fonte e sincronizando "${knowledgeBase.name}"...`
          : `Sincronização iniciada para "${knowledgeBase.name}".`
      );
      scheduleStatusRefresh();
    } catch (err) {
      setSyncError(getApiErrorMessage(err, 'Não foi possível iniciar a sincronização.'));
      setKb((current) => (current ? { ...current, status: previousStatus } : current));
    } finally {
      setIsSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="surface-card px-4 py-12 text-center">
        <p className="text-content-muted text-sm">Carregando base...</p>
      </div>
    );
  }

  if (error || !kb) {
    return (
      <div className="space-y-4">
        <Link to="/knowledge-bases" className="text-sm text-forge-600 hover:text-forge-800">
          ← Voltar para bases
        </Link>
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error || 'Base não encontrada.'}</p>
        </div>
      </div>
    );
  }

  const ingest = kb.configurations?.ingest || {};
  const sourceReference = buildSourceReference(kb);
  const syncSummaryText = formatSyncSummary(kb.source_sync_summary || ingest.source_sync_summary);
  const ingestError = ingest.error;
  const expectedDocuments = ingest.expected_documents;
  const documentsCompleted = ingest.documents_completed ?? 0;
  const documentsFailed = ingest.documents_failed ?? 0;
  const importJobStatus = filesData.import_job_status || ingest.import_job_status;
  const syncProgress = kb.sync_progress || ingest.sync_progress;
  const files = filesData.files || [];
  const lastSourceCheckAt =
    filesData.last_source_check_at || ingest.last_source_check_at || null;
  const showSourceCheckHint =
    (kb.source_type === 'google_drive' || kb.source_type === 's3')
    && isSourceCheckStale(lastSourceCheckAt);
  const isExternalLink =
    kb.source_type === 'url' || kb.source_type === 'google_drive';

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <Link to="/knowledge-bases" className="text-sm text-forge-600 hover:text-forge-800">
          ← Voltar para bases
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <h1 className="text-2xl font-bold text-content-primary">{kb.name}</h1>
              <KnowledgeStatusBadge status={kb.status} />
            </div>
            <p className="text-sm text-content-muted mt-1">
              {SOURCE_TYPE_LABELS[kb.source_type] || kb.source_type} · {kb.chunk_count ?? 0} chunks
            </p>
          </div>
          <SyncButton
            kb={kb}
            isSyncing={isSyncing}
            onSync={handleSync}
            className="btn-secondary text-sm px-4 py-2.5 disabled:opacity-40 whitespace-nowrap shrink-0"
          />
        </div>
      </div>

      {kb.status === 'syncing' && (
        <p className="text-sm text-violet-800 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
          {syncProgress?.documents_total
            ? `Indexando no OpenSearch: ${syncProgress.documents_done ?? 0} de ${syncProgress.documents_total} documentos (${syncProgress.indexed_chunks ?? kb.chunk_count ?? 0} chunks). Isso pode levar vários minutos com bases grandes.`
            : 'Indexando conteúdo no OpenSearch. Isso pode levar vários minutos com bases grandes.'}
        </p>
      )}

      {syncMessage && (
        <p className="text-sm text-forge-700 bg-forge-50 border border-forge-200 rounded-xl px-4 py-3">
          {syncMessage}
        </p>
      )}

      {syncError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{syncError}</p>
        </div>
      )}

      {usageTypeError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{usageTypeError}</p>
        </div>
      )}

      {(kb.status === 'error' || ingestError) && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm font-medium text-red-800">Erro no processamento</p>
          {ingestError && (
            <p className="text-sm text-red-700 mt-1">{ingestError}</p>
          )}
        </div>
      )}

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-content-primary">Visão geral</h2>
          <p className="text-xs text-content-muted mt-1">Metadados e origem da base</p>
        </div>

        <div className="surface-card px-4 py-4">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <OverviewField label="Origem">
              {SOURCE_TYPE_LABELS[kb.source_type] || kb.source_type}
            </OverviewField>
            <OverviewField label="Referência">
              {isExternalLink ? (
                <a
                  href={sourceReference}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-forge-600 hover:text-forge-800 underline break-all"
                >
                  {sourceReference}
                </a>
              ) : (
                sourceReference
              )}
            </OverviewField>
            <OverviewField label="Chunks indexados">{kb.chunk_count ?? 0}</OverviewField>
            <OverviewField label="Índice OpenSearch">
              <code className="text-xs bg-slate-100 px-2 py-1 rounded">{kb.opensearch_index || '—'}</code>
            </OverviewField>
            <OverviewField label="Criado em">{formatDate(kb.created_at)}</OverviewField>
            <OverviewField label="Atualizado em">{formatDate(kb.updated_at)}</OverviewField>
          </dl>
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-content-primary">Sincronização</h2>
          <p className="text-xs text-content-muted mt-1">Histórico e progresso de ingestão</p>
        </div>

        <div className="surface-card px-4 py-4">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <OverviewField label="Última verificação na fonte">
              {formatDate(kb.last_source_check_at || ingest.last_source_check_at)}
            </OverviewField>
            <OverviewField label="Última sincronização">
              {formatDate(kb.last_synced_at || ingest.last_synced_at)}
            </OverviewField>
            <OverviewField label="Resumo da última sync">
              {syncSummaryText || '—'}
            </OverviewField>
            <OverviewField label="Status do import job">
              {importJobStatus || '—'}
            </OverviewField>
            <OverviewField label="Documentos processados">
              {expectedDocuments != null
                ? `${documentsCompleted} / ${expectedDocuments}`
                : documentsCompleted}
            </OverviewField>
            <OverviewField label="Documentos com falha">{documentsFailed}</OverviewField>
            {syncProgress && (
              <OverviewField label="Progresso da indexação">
                {`${syncProgress.documents_done ?? 0} / ${syncProgress.documents_total ?? '—'} documentos · ${syncProgress.indexed_chunks ?? kb.chunk_count ?? 0} chunks`}
              </OverviewField>
            )}
          </dl>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-content-primary">Arquivos</h2>
            <p className="text-xs text-content-muted mt-1">
              Documentos importados e indexados na base. Configure o uso após o processamento.
            </p>
          </div>
          <p className="text-sm text-content-subtle">
            {files.length} arquivo{files.length !== 1 ? 's' : ''}
          </p>
        </div>

        {filesError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-red-700">{filesError}</p>
            <button type="button" onClick={() => loadFiles()} className="btn-secondary text-sm px-3 py-2">
              Tentar novamente
            </button>
          </div>
        )}

        {showSourceCheckHint && (
          <p className="text-xs text-content-muted bg-slate-50 border border-surface-border rounded-lg px-3 py-2">
            A presença dos arquivos na fonte é atualizada ao sincronizar a base.
          </p>
        )}

        {filesLoading ? (
          <div className="surface-card px-4 py-10 text-center">
            <p className="text-content-muted text-sm">Carregando arquivos...</p>
          </div>
        ) : files.length === 0 && !filesError ? (
          <EmptyState
            icon={EMPTY_STATE_ICONS.files}
            title={
              isProcessingStatus(kb.status)
                ? 'Aguardando processamento'
                : 'Nenhum arquivo encontrado'
            }
            description={
              isProcessingStatus(kb.status)
                ? 'Os arquivos aparecerão aqui após a indexação.'
                : 'Os arquivos importados aparecerão aqui após a sincronização.'
            }
          />
        ) : (
          <FilesTable
            files={files}
            savingDocumentId={savingDocumentId}
            onUsageTypeChange={handleFileUsageTypeChange}
          />
        )}
      </section>
    </div>
  );
}

export default KnowledgeBaseShow;
